/* oxlint-disable typescript/consistent-type-assertions -- lint to the future batch ignore */
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import { TextField } from '@repo/design-system-kit/components/text-field/text-field';
import { hasMFAError, type ScaError } from '@repo/sca/utils/sca-errors';
import { useMutation } from '@tanstack/react-query';
import { HTTP_STATUS_CODES } from 'register-partners-js/constants/http-status-codes';
import styles from './login-form.module.css';

interface FormValues {
  email: string;
  password: string;
}

interface LoginPageProps {
  authenticate: (email: string, password: string) => Promise<void>;
  onForgotPasswordClick: () => void;
}

interface ErrorWithResponseJSON {
  status?: number;
  responseJSON?: {
    errors?: unknown;
  };
}

function hasResponseJSON(error: unknown): error is ErrorWithResponseJSON {
  return typeof error === 'object' && error !== null && 'responseJSON' in error;
}

export function LoginForm({ authenticate, onForgotPasswordClick }: LoginPageProps): ReactNode {
  const { formatMessage } = useIntl();

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    clearErrors,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  });

  const authenticateMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      try {
        await authenticate(data.email, data.password);
      } catch (error) {
        if (hasResponseJSON(error) && hasMFAError(error.responseJSON?.errors as ScaError)) {
          throw error.responseJSON;
        }

        const { status } = error as { status?: number };

        if (status === HTTP_STATUS_CODES.UNAUTHORIZED) {
          setError('password', {
            message: formatMessage({ id: 'errors.invalid-credentials' }),
          });
          return;
        }

        if (status === HTTP_STATUS_CODES.FORBIDDEN) {
          setError('password', {
            message: formatMessage({ id: 'errors.account-locked' }),
          });
        }
      }
    },
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    await authenticateMutation.mutateAsync(data);
  };

  return (
    <form
      className={cx(styles.loginForm, 'mb-32')}
      data-test-login-form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="mb-32">
        <Controller
          control={control}
          name="email"
          render={({ field: { ref: _, onChange, value }, fieldState: { error, invalid } }) => (
            <TextField
              autoComplete="username"
              className="mb-16"
              data-test-login-username
              errorMessage={invalid ? error?.message : undefined}
              isDisabled={isSubmitting}
              isInvalid={invalid}
              label={formatMessage({ id: 'generic.email-address.label' })}
              onChange={newValue => {
                onChange(newValue);
                clearErrors(['email', 'password']);
              }}
              placeholder={formatMessage({ id: 'placeholders.email' })}
              type="email"
              value={value}
            />
          )}
          rules={{
            required: formatMessage({ id: 'generic.required-field.error' }),
          }}
        />
        <Controller
          control={control}
          name="password"
          render={({ field: { ref: _, onChange, value }, fieldState: { error, invalid } }) => (
            <TextField
              autoComplete="current-password"
              data-test-login-password
              errorMessage={invalid ? error?.message : undefined}
              isDisabled={isSubmitting}
              isInvalid={invalid}
              label={formatMessage({ id: 'generic.password.label' })}
              onChange={newValue => {
                onChange(newValue);
                clearErrors(['password']);
              }}
              placeholder={formatMessage({ id: 'placeholders.password' })}
              type="password"
              value={value}
            />
          )}
          rules={{
            required: formatMessage({ id: 'generic.required-field.error' }),
          }}
        />
      </div>
      <Button
        className={cx('btn btn--primary', styles.signInBtn)}
        data-test-login-submit
        isDisabled={isSubmitting}
        isLoading={isSubmitting}
        type="submit"
      >
        {formatMessage({ id: 'generic.sign-in.cta' })}
      </Button>
      <Button
        data-test-login-forgot-btn
        onPress={onForgotPasswordClick}
        type="button"
        variant="tertiary"
      >
        {formatMessage({ id: 'sign-in.forgot-password' })}
      </Button>
    </form>
  );
}
